import { useCallback, useEffect, useRef } from "react";
import type { MouseEvent, PointerEvent } from "react";

type Gesture = {
  pointerId: number;
  x: number;
  y: number;
  canvas: HTMLCanvasElement;
  dragged: boolean;
};

type Props = {
  picking: boolean;
  placePin: (clientX: number, clientY: number) => boolean;
};

export const usePinPlacement = ({ picking, placePin }: Props) => {
  const current = useRef({ picking, placePin });
  current.current = { picking, placePin };
  const gesture = useRef<Gesture | null>(null);
  const lastPlacement = useRef<{ x: number; y: number; until: number } | null>(
    null,
  );
  const isFollowupClick = useCallback(
    (event: { timeStamp: number; clientX: number; clientY: number }) => {
      const last = lastPlacement.current;
      return Boolean(
        last &&
        event.timeStamp <= last.until &&
        Math.hypot(event.clientX - last.x, event.clientY - last.y) <= 6,
      );
    },
    [],
  );
  const cancelGesture = useCallback(() => {
    gesture.current = null;
  }, []);

  useEffect(() => {
    cancelGesture();
  }, [picking, cancelGesture]);

  useEffect(() => {
    window.addEventListener("blur", cancelGesture);
    return () => window.removeEventListener("blur", cancelGesture);
  }, [cancelGesture]);

  useEffect(() => {
    // The drawer can open under the second click, outside the original map target.
    const stopFollowupClick = (event: globalThis.MouseEvent) => {
      if (!isFollowupClick(event)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    };
    document.addEventListener("click", stopFollowupClick, true);
    document.addEventListener("dblclick", stopFollowupClick, true);
    return () => {
      document.removeEventListener("click", stopFollowupClick, true);
      document.removeEventListener("dblclick", stopFollowupClick, true);
    };
  }, [isFollowupClick]);

  const shouldIgnoreClick = useCallback(
    (event: { timeStamp: number; clientX: number; clientY: number }) =>
      current.current.picking || isFollowupClick(event),
    [isFollowupClick],
  );
  const stopPlacementClick = (event: MouseEvent<HTMLDivElement>) => {
    if (!shouldIgnoreClick(event)) return;
    event.preventDefault();
    event.stopPropagation();
  };

  return {
    shouldIgnoreClick,
    events: {
      onPointerDown: (event: PointerEvent<HTMLDivElement>) => {
        if (
          !current.current.picking ||
          !event.isPrimary ||
          event.button !== 0 ||
          gesture.current ||
          !(event.target instanceof HTMLCanvasElement)
        ) {
          cancelGesture();
          return;
        }
        gesture.current = {
          pointerId: event.pointerId,
          x: event.clientX,
          y: event.clientY,
          canvas: event.target,
          dragged: false,
        };
      },
      onPointerMove: (event: PointerEvent<HTMLDivElement>) => {
        const press = gesture.current;
        if (!press || event.pointerId !== press.pointerId) return;
        if (Math.hypot(event.clientX - press.x, event.clientY - press.y) > 6)
          press.dragged = true;
      },
      onPointerUp: (event: PointerEvent<HTMLDivElement>) => {
        const press = gesture.current;
        if (!press || event.pointerId !== press.pointerId) return;
        cancelGesture();
        if (
          !current.current.picking ||
          press.dragged ||
          Math.hypot(event.clientX - press.x, event.clientY - press.y) > 6
        )
          return;
        const bounds = press.canvas.getBoundingClientRect();
        if (
          event.clientX < bounds.left ||
          event.clientX > bounds.right ||
          event.clientY < bounds.top ||
          event.clientY > bounds.bottom
        )
          return;
        // Bubble after the map controls release pointer capture, before opening the drawer.
        if (current.current.placePin(event.clientX, event.clientY))
          lastPlacement.current = {
            x: event.clientX,
            y: event.clientY,
            until: event.timeStamp + 500,
          };
      },
      onPointerCancel: cancelGesture,
      onPointerLeave: cancelGesture,
      onLostPointerCapture: cancelGesture,
      onWheel: cancelGesture,
      onContextMenu: cancelGesture,
      onClickCapture: stopPlacementClick,
      onDoubleClickCapture: stopPlacementClick,
    },
  };
};
